import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import GenericModal from '../../../../ui/GenericModal';
import { Button, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import type { KidLayoutLevel } from '../../../../../api/kids/types';
import { useAllFundManagersForClient } from '../../../../../api/rs3-public/raptorPlatform';


type SaveLayoutTemplateFormValues = {
  layoutName: string;
  layoutLevel: KidLayoutLevel;
  fundManagerId?: string;
};

export type SaveLayoutTemplateSubmission = {
  layoutName: string;
  layoutLevel: KidLayoutLevel;
  layoutValue: string;
};

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  clientId: string;
  isSaving: boolean;
  onSubmit: (values: SaveLayoutTemplateSubmission) => Promise<void>;
};

export default function SaveLayoutTemplateForm({
  isOpen,
  setIsOpen,
  clientId,
  isSaving,
  onSubmit,
}: Props) {

  const { data: allFundManagers, isLoading: allFundManagersIsLoading, error: allFundManagersError } = useAllFundManagersForClient(clientId);


  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<SaveLayoutTemplateFormValues>({
    defaultValues: {
      layoutName: '',
      layoutLevel: 'client',
      fundManagerId: '',
    },
    mode: 'onSubmit',
  });

  const layoutLevel = watch('layoutLevel');

  useEffect(() => {
    if (!isOpen) return;
    reset({
      layoutName: '',
      layoutLevel: 'client',
      fundManagerId: '',
    });
  }, [isOpen, reset]);

  const submit = async (values: SaveLayoutTemplateFormValues) => {
    const layoutValue =
      values.layoutLevel === 'fund_manager'
        ? values.fundManagerId || ''
        : clientId;

    await onSubmit({
      layoutName: values.layoutName.trim(),
      layoutLevel: values.layoutLevel,
      layoutValue,
    });
    setIsOpen(false);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Save Layout Template"
      description="Create a reusable KID layout template from the current editor form."
      className="max-w-lg"
    >
      <form className="space-y-4" onSubmit={handleSubmit(submit)}>
        <div>
          <label
            htmlFor="layout-template-name"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Template name
          </label>
          <input
            id="layout-template-name"
            type="text"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
            placeholder="Enter template name"
            {...register('layoutName', {
              required: 'Please enter a template name.',
              validate: (v) =>
                v.trim().length > 0 || 'Please enter a template name.',
            })}
          />
          {errors.layoutName?.message && (
            <p className="mt-1 text-xs text-red-600">{errors.layoutName.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="layout-template-level"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Template type
          </label>
          <select
            id="layout-template-level"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
            {...register('layoutLevel', { required: true })}
          >
            <option value="client">Client</option>
            <option value="sub_client">Sub-client</option>
            <option value="fund_manager">Fund manager</option>
          </select>
        </div>

        {layoutLevel === 'fund_manager' && (
          <div>
            <label
              htmlFor="layout-template-fund-manager"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Fund manager
            </label>
            {allFundManagersIsLoading ? (
              <div className="py-2 text-gray-500 text-sm">
                Loading fund managers...
              </div>
            ) : allFundManagersError ? (
              <div className="py-2 text-red-600 text-sm">
                Failed to load fund managers. Please try again.
              </div>
            ) : (
              <select
                id="layout-template-fund-manager"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-mainblue focus:outline-none focus:ring-1 focus:ring-mainblue"
                {...register('fundManagerId', {
                  validate: (v, allValues) =>
                    allValues.layoutLevel !== 'fund_manager' ||
                    (!!v && v.length > 0) ||
                    'Please select a fund manager for this template.',
                })}
              >
                <option value="">Select a fund manager</option>
                {allFundManagers?.filter((fm) => fm.fund_manager_is_active)
                  .sort((a, b) => a.fund_manager_name.localeCompare(b.fund_manager_name))
                  .map((fm) => (
                    <option key={fm.fund_manager_id} value={fm.fund_manager_id}>
                      {fm.fund_manager_name}
                    </option>
                  ))}
              </select>
            )}
            {errors.fundManagerId?.message && (
              <p className="mt-1 text-xs text-red-600">
                {errors.fundManagerId.message}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            onClick={() => setIsOpen(false)}
            disabled={isSaving}
            variant="outlined"
            size="small"
            className="normal-case"
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaving}
            variant="contained"
            disableElevation
            size="small"
            className="normal-case !text-white bg-mainblue hover:bg-mainblue/90 disabled:!bg-mainblue disabled:!opacity-40"
            startIcon={
              isSaving ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <SaveIcon style={{ fontSize: 14 }} />
              )
            }
          >
            Save Template
          </Button>
        </div>
      </form>
    </GenericModal>
  );
}

